<template>
    <div class="main-content">
        <header class="content-header mb-4 mt-n2">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">BI & Analytics</li>
                </ol>
            </nav>
        </header>

        <div class="d-flex flex-column align-items-center mb-5">
            <h4 class="mb-3 fw-bold text-dark">
                <i class="bi bi-graph-up-arrow text-primary me-2"></i> Dashboard Financeiro
            </h4>

            <div class="btn-group shadow-sm bg-white p-1 rounded-pill border" role="group">
                <input type="radio" class="btn-check" name="tab" id="tab-dre" :checked="activeTab === 'dre'"
                    @click="activeTab = 'dre'">
                <label class="btn btn-outline-primary border-0 rounded-pill px-4 py-2 fw-bold" for="tab-dre"
                    :class="activeTab === 'dre' ? 'active' : 'text-muted'">
                    <i class="bi bi-calculator me-2"></i> DRE Gerencial
                </label>

                <input type="radio" class="btn-check" name="tab" id="tab-bonus" :checked="activeTab === 'bonus'"
                    @click="activeTab = 'bonus'">
                <label class="btn btn-outline-primary border-0 rounded-pill px-4 py-2 fw-bold" for="tab-bonus"
                    :class="activeTab === 'bonus' ? 'active' : 'text-muted'">
                    <i class="bi bi-lightning-charge-fill me-2"></i> Eficiência de Cobrança
                </label>
            </div>
        </div>

        <transition name="fade" mode="out-in">

            <div v-if="activeTab === 'dre'" key="dre-tab">

                <div class="card p-4 shadow-sm rounded-4 border-0 mb-4">
                    <form @submit.prevent="fetchDreData" class="row g-3 align-items-end">
                        <div class="col-md-3">
                            <label class="form-label small fw-bold text-muted">Data Inicial</label>
                            <input type="date" class="form-control form-control-pmg" v-model="dreFilters.start_date"
                                required>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small fw-bold text-muted">Data Final</label>
                            <input type="date" class="form-control form-control-pmg" v-model="dreFilters.end_date"
                                required>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label small fw-bold text-muted">Centro de Custo</label>
                            <select class="form-select form-control-pmg" v-model="dreFilters.cost_center_id">
                                <option value="">Todos os Centros (Visão Geral)</option>
                                <option v-for="cc in costCenters" :key="cc.id" :value="cc.id">{{ cc.name }}</option>
                            </select>
                        </div>
                        <div class="col-md-2">
                            <button type="submit" class="btn btn-pmg-primary w-100 fw-bold" :disabled="loadingDre">
                                <span v-if="loadingDre" class="spinner-border spinner-border-sm me-2" role="status"
                                    aria-hidden="true"></span>
                                <i v-else class="bi bi-funnel me-1"></i>
                                {{ loadingDre ? 'Processando...' : 'Filtrar DRE' }}
                            </button>
                        </div>
                    </form>
                </div>

                <div class="card border-0 shadow-sm rounded-4 overflow-hidden bg-white position-relative mb-4">
                    <div v-if="loadingDre"
                        class="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
                        style="z-index: 10;">
                        <div class="spinner-border text-primary" role="status"></div>
                    </div>

                    <div v-if="dreData" class="table-responsive">
                        <table class="table table-hover table-borderless mb-0 align-middle" style="font-size: 0.95rem;">
                            <tbody>
                                <tr class="bg-light border-bottom">
                                    <td class="fw-bold text-dark ps-4 py-3">1. Receita Bruta</td>
                                    <td class="text-end fw-bold text-success py-3 pe-4">R$ {{
                                        formatCurrency(dreData.gross_revenue.total) }}</td>
                                </tr>
                                <tr v-for="item in dreData.gross_revenue.items" :key="item.name">
                                    <td class="ps-5 text-muted small py-2"><i
                                            class="bi bi-arrow-return-right me-2 opacity-50"></i>{{ item.name }}</td>
                                    <td class="text-end text-muted small py-2 pe-4">R$ {{ formatCurrency(item.value) }}
                                    </td>
                                </tr>

                                <tr class="bg-light border-top border-bottom">
                                    <td class="fw-bold text-dark ps-4 py-3">2. (-) Deduções e Impostos</td>
                                    <td class="text-end fw-bold text-danger py-3 pe-4">- R$ {{
                                        formatCurrency(dreData.deductions.total) }}</td>
                                </tr>

                                <tr style="background-color: #f8fafc; border-top: 2px solid #e2e8f0;">
                                    <td class="fw-bold text-primary fs-6 ps-4 py-3">3. (=) Receita Líquida</td>
                                    <td class="text-end fw-bold text-primary fs-6 py-3 pe-4">R$ {{
                                        formatCurrency(dreData.net_revenue) }}</td>
                                </tr>

                                <tr class="bg-light border-top border-bottom">
                                    <td class="fw-bold text-dark ps-4 py-3">4. (-) Custos Variáveis</td>
                                    <td class="text-end fw-bold text-danger py-3 pe-4">- R$ {{
                                        formatCurrency(dreData.variable_costs.total) }}</td>
                                </tr>
                                <tr v-for="item in dreData.variable_costs.items" :key="item.name">
                                    <td class="ps-5 text-muted small py-2"><i
                                            class="bi bi-arrow-return-right me-2 opacity-50"></i>{{ item.name }}</td>
                                    <td class="text-end text-muted small py-2 pe-4">R$ {{ formatCurrency(item.value) }}
                                    </td>
                                </tr>

                                <tr style="background-color: #f8fafc; border-top: 2px solid #e2e8f0;">
                                    <td class="fw-bold text-primary fs-6 ps-4 py-3">5. (=) Margem de Contribuição</td>
                                    <td class="text-end fw-bold text-primary fs-6 py-3 pe-4">
                                        <span class="badge bg-primary me-3 fs-6">{{
                                            calculatePercentage(dreData.contribution_margin, dreData.net_revenue)
                                            }}%</span>
                                        R$ {{ formatCurrency(dreData.contribution_margin) }}
                                    </td>
                                </tr>

                                <tr class="bg-light border-top border-bottom">
                                    <td class="fw-bold text-dark ps-4 py-3">6. (-) Custos Fixos / Despesas Operacionais
                                    </td>
                                    <td class="text-end fw-bold text-danger py-3 pe-4">- R$ {{
                                        formatCurrency(dreData.fixed_costs.total) }}</td>
                                </tr>
                                <tr v-for="item in dreData.fixed_costs.items" :key="item.name">
                                    <td class="ps-5 text-muted small py-2"><i
                                            class="bi bi-arrow-return-right me-2 opacity-50"></i>{{ item.name }}</td>
                                    <td class="text-end text-muted small py-2 pe-4">R$ {{ formatCurrency(item.value) }}
                                    </td>
                                </tr>

                                <tr :class="dreData.operating_result >= 0 ? 'bg-success-subtle' : 'bg-danger-subtle'"
                                    style="border-top: 3px solid #cbd5e1;">
                                    <td class="fw-bold fs-5 ps-4 py-4"
                                        :class="dreData.operating_result >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'">
                                        7. (=) Resultado Operacional
                                    </td>
                                    <td class="text-end fw-bold fs-5 py-4 pe-4"
                                        :class="dreData.operating_result >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'">
                                        R$ {{ formatCurrency(dreData.operating_result) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-else-if="!loadingDre" class="text-center py-5 text-muted">
                        <i class="bi bi-file-earmark-bar-graph fs-1 d-block mb-3 opacity-25"></i>
                        <p class="mb-0 fw-medium">Selecione um período para visualizar o DRE.</p>
                    </div>
                </div>
            </div>

            <div v-else-if="activeTab === 'bonus'" key="bonus-tab">

                <div class="card p-4 shadow-sm rounded-4 border-0 mb-4">
                    <form @submit.prevent="fetchMetrics" class="row g-3 align-items-end">
                        <div class="col-md-3">
                            <label class="form-label small fw-bold text-muted">Data Inicial</label>
                            <input type="date" class="form-control form-control-pmg" v-model="filters.start_date"
                                required>
                        </div>
                        <div class="col-md-3">
                            <label class="form-label small fw-bold text-muted">Data Final</label>
                            <input type="date" class="form-control form-control-pmg" v-model="filters.end_date"
                                required>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted">Desconto Mín (%)</label>
                            <input type="number" class="form-control form-control-pmg" v-model="filters.min_percentage"
                                placeholder="Ex: 5">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted">Desconto Máx (%)</label>
                            <input type="number" class="form-control form-control-pmg" v-model="filters.max_percentage"
                                placeholder="Ex: 15">
                        </div>
                        <div class="col-md-2">
                            <button type="submit" class="btn btn-pmg-primary w-100 fw-bold" :disabled="loading">
                                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                                    aria-hidden="true"></span>
                                <i v-else class="bi bi-funnel me-1"></i>
                                {{ loading ? 'Buscando...' : 'Filtrar' }}
                            </button>
                        </div>
                    </form>
                </div>
                <h5 class="fw-bold mb-3 mt-2 text-dark border-bottom pb-2" v-if="metrics">
                    <i class="bi bi-wallet2 text-primary me-2"></i> Visão Geral do Período
                </h5>

                <div class="row g-4 mb-5" v-if="metrics && metrics.general_stats">

                    <!-- 1. Total Gerado -->
                    <div class="col-md-3">
                        <div class="card border-0 shadow-sm rounded-4 h-100"
                            style="border-left: 4px solid #64748b !important;">
                            <div class="card-body p-4">
                                <div class="d-flex justify-content-between align-items-start mb-2">
                                    <h6 class="text-muted fw-bold mb-0 text-uppercase" style="font-size: 0.75rem;">
                                        Cobranças Geradas</h6>
                                    <div class="bg-secondary bg-opacity-10 text-secondary rounded d-flex align-items-center justify-content-center"
                                        style="width: 32px; height: 32px;">
                                        <i class="bi bi-receipt"></i>
                                    </div>
                                </div>
                                <h3 class="fw-bold text-dark mb-1">{{ formatCurrency(metrics.general_stats.total.value)
                                    }}</h3>
                                <p class="text-muted small mb-0"><span class="fw-bold text-dark">{{
                                        metrics.general_stats.total.count }}</span> cobranças emitidas</p>
                            </div>
                        </div>
                    </div>

                    <!-- 2. Pagas (Com % do Valor e % da Quantidade) -->
                    <div class="col-md-3">
                        <div class="card border-0 shadow-sm rounded-4 h-100"
                            style="border-left: 4px solid #10b981 !important;">
                            <div class="card-body p-4">
                                <div class="d-flex justify-content-between align-items-start mb-2">
                                    <h6 class="text-muted fw-bold mb-0 text-uppercase" style="font-size: 0.75rem;">
                                        Recebido (Pagas)</h6>
                                    <div class="bg-success bg-opacity-10 text-success rounded d-flex align-items-center justify-content-center"
                                        style="width: 32px; height: 32px;">
                                        <i class="bi bi-check-circle"></i>
                                    </div>
                                </div>
                                <div class="d-flex align-items-baseline mb-1 flex-wrap gap-1">
                                    <h3 class="fw-bold text-success-emphasis mb-0">{{
                                        formatCurrency(metrics.general_stats.paid.value) }}</h3>
                                    <!-- Badge de % do Valor Recebido -->
                                    <span class="badge bg-success-subtle text-success rounded-pill fw-bold"
                                        style="font-size: 0.7rem;">
                                        {{ metrics.general_stats.paid.percentage_value }}%
                                    </span>
                                </div>
                                <p class="text-muted small mb-0">
                                    <span class="fw-bold text-dark">{{ metrics.general_stats.paid.count }}</span>
                                    liquidadas
                                    <span class="text-muted" style="font-size: 0.75rem;">({{
                                        metrics.general_stats.paid.percentage_count }}% das faturas)</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- 3. Pendentes -->
                    <div class="col-md-3">
                        <div class="card border-0 shadow-sm rounded-4 h-100"
                            style="border-left: 4px solid #3b82f6 !important;">
                            <div class="card-body p-4">
                                <div class="d-flex justify-content-between align-items-start mb-2">
                                    <h6 class="text-muted fw-bold mb-0 text-uppercase" style="font-size: 0.75rem;">A
                                        Receber (No Prazo)</h6>
                                    <div class="bg-primary bg-opacity-10 text-primary rounded d-flex align-items-center justify-content-center"
                                        style="width: 32px; height: 32px;">
                                        <i class="bi bi-hourglass-split"></i>
                                    </div>
                                </div>
                                <div class="d-flex align-items-baseline mb-1 flex-wrap gap-1">
                                    <h3 class="fw-bold text-primary-emphasis mb-0">{{
                                        formatCurrency(metrics.general_stats.pending.value) }}</h3>
                                    <!-- Badge de % do Valor Pendente -->
                                    <span class="badge bg-primary-subtle text-primary rounded-pill fw-bold"
                                        style="font-size: 0.7rem;">
                                        {{ metrics.general_stats.pending.percentage_value }}%
                                    </span>
                                </div>
                                <p class="text-muted small mb-0">
                                    <span class="fw-bold text-dark">{{ metrics.general_stats.pending.count }}</span> em
                                    aberto
                                    <span class="text-muted" style="font-size: 0.75rem;">({{
                                        metrics.general_stats.pending.percentage_count }}% das faturas)</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- 4. Em Atraso -->
                    <div class="col-md-3">
                        <div class="card border-0 shadow-sm rounded-4 h-100"
                            style="border-left: 4px solid #ef4444 !important;">
                            <div class="card-body p-4">
                                <div class="d-flex justify-content-between align-items-start mb-2">
                                    <h6 class="text-muted fw-bold mb-0 text-uppercase" style="font-size: 0.75rem;">Em
                                        Atraso</h6>
                                    <div class="bg-danger bg-opacity-10 text-danger rounded d-flex align-items-center justify-content-center"
                                        style="width: 32px; height: 32px;">
                                        <i class="bi bi-exclamation-triangle"></i>
                                    </div>
                                </div>
                                <div class="d-flex align-items-baseline mb-1 flex-wrap gap-1">
                                    <h3 class="fw-bold text-danger-emphasis mb-0">{{
                                        formatCurrency(metrics.general_stats.overdue.value) }}</h3>
                                    <!-- Badge de % do Valor em Atraso -->
                                    <span class="badge bg-danger-subtle text-danger rounded-pill fw-bold"
                                        style="font-size: 0.7rem;">
                                        {{ metrics.general_stats.overdue.percentage_value }}%
                                    </span>
                                </div>
                                <p class="text-muted small mb-0">
                                    <span class="fw-bold text-dark">{{ metrics.general_stats.overdue.count }}</span>
                                    vencidas
                                    <span class="text-muted" style="font-size: 0.75rem;">({{
                                        metrics.general_stats.overdue.percentage_count }}% das faturas)</span>
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
                <h5 class="fw-bold mb-3 mt-4 text-dark border-bottom pb-2" v-if="metrics">
                    <i class="bi bi-pie-chart text-primary me-2"></i> Visão de Fracionamento
                </h5>

                <div class="row g-4 mb-5" v-if="metrics && metrics.fractional_stats">
                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-lg rounded-4 h-100 position-relative overflow-hidden"
                            style="background-color: #475569;">
                            <div class="position-absolute top-0 end-0 opacity-10 mt-n3 me-n3">
                                <i class="bi bi-ui-checks-grid" style="font-size: 8rem; color: #ffffff;"></i>
                            </div>
                            <div class="card-body p-4 d-flex flex-column justify-content-between position-relative z-1">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-white-50 fw-semibold mb-1">Permitem Fracionar</h6>
                                        <h3 class="fw-bold text-white mb-0">{{
                                            metrics.fractional_stats.fractionable_count }}</h3>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-white-50 small"><i class="bi bi-gear me-1"></i>Cobranças
                                        configuradas</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-lg rounded-4 h-100 position-relative overflow-hidden"
                            style="background-color: #0369a1;">
                            <div class="position-absolute top-0 end-0 opacity-10 mt-n3 me-n3">
                                <i class="bi bi-activity" style="font-size: 8rem; color: #ffffff;"></i>
                            </div>
                            <div class="card-body p-4 d-flex flex-column justify-content-between position-relative z-1">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-white-50 fw-semibold mb-1">Cobranças Ativas</h6>
                                        <h3 class="fw-bold text-white mb-0">{{
                                            metrics.fractional_stats.active_fractional }}</h3>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-white fw-semibold">
                                        {{ calculatePercentage(metrics.fractional_stats.active_fractional,
                                            metrics.fractional_stats.fractionable_count) }}%
                                    </span>
                                    <span class="text-white-50 small"> de engajamento</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-lg rounded-4 h-100 position-relative overflow-hidden"
                            style="background-color: #047857;">
                            <div class="position-absolute top-0 end-0 opacity-10 mt-n3 me-n3">
                                <i class="bi bi-safe" style="font-size: 8rem; color: #ffffff;"></i>
                            </div>
                            <div class="card-body p-4 d-flex flex-column justify-content-between position-relative z-1">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-white-50 fw-semibold mb-1">Caixa Antecipado</h6>
                                        <h3 class="fw-bold text-white mb-0">{{
                                            formatCurrency(metrics.fractional_stats.total_collected) }}</h3>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-white-50 small"><i class="bi bi-check2-circle me-1"></i>Apurado
                                        via fracionamento</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-lg rounded-4 h-100 position-relative overflow-hidden"
                            style="background-color: #561569;">
                            <div class="position-absolute top-0 end-0 opacity-10 mt-n3 me-n3">
                                <i class="bi bi-cash-stack" style="font-size: 8rem; color: #ffffff;"></i>
                            </div>
                            <div class="card-body p-4 d-flex flex-column justify-content-between position-relative z-1">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-white-50 fw-semibold mb-1">Projeção Líquida Atual</h6>
                                        <h3 class="fw-bold text-white mb-0">{{
                                            formatCurrency(metrics.fractional_stats.projected_receivables) }}</h3>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-white-50 small"><i class="bi bi-info-circle me-1"></i>Já prevê os
                                        descontos de hoje</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row g-4 mb-4" v-if="metrics">
                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-sm rounded-4 h-100">
                            <div class="card-body p-4 d-flex flex-column justify-content-between">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-muted fw-semibold mb-1">Taxa de Adesão</h6>
                                        <h3 class="fw-bold text-dark mb-0">{{ metrics.bonus_effectiveness.adoption_rate
                                            }}%</h3>
                                    </div>
                                    <div class="icon-shape bg-primary-subtle text-primary rounded-circle">
                                        <i class="bi bi-graph-up-arrow fs-5"></i>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-muted small">De <span class="fw-bold text-dark">{{
                                        metrics.bonus_effectiveness.eligible_invoices }}</span> faturas
                                        elegíveis</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-sm rounded-4 h-100">
                            <div class="card-body p-4 d-flex flex-column justify-content-between">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-muted fw-semibold mb-1">Descontos (R$)</h6>
                                        <h3 class="fw-bold text-dark mb-0">{{
                                            formatCurrency(metrics.bonus_effectiveness.total_discount_granted) }}</h3>
                                    </div>
                                    <div class="icon-shape bg-success-subtle text-success rounded-circle">
                                        <i class="bi bi-cash-coin fs-5"></i>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-success small fw-semibold"><i
                                            class="bi bi-check2-circle me-1"></i>{{
                                                metrics.bonus_effectiveness.discount_used_count }}</span>
                                    <span class="text-muted small"> clientes aproveitaram</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-sm rounded-4 h-100">
                            <div class="card-body p-4 d-flex flex-column justify-content-between">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-muted fw-semibold mb-1">Prazos Estendidos</h6>
                                        <h3 class="fw-bold text-dark mb-0">{{
                                            metrics.bonus_effectiveness.deadline_used_count }}</h3>
                                    </div>
                                    <div class="icon-shape bg-info-subtle text-info rounded-circle">
                                        <i class="bi bi-calendar-plus fs-5"></i>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-muted small">Adesões ao bônus de dias</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="card kpi-card border-0 shadow-sm rounded-4 h-100">
                            <div class="card-body p-4 d-flex flex-column justify-content-between">
                                <div class="d-flex align-items-start justify-content-between mb-3">
                                    <div>
                                        <h6 class="text-muted fw-semibold mb-1">Pagamentos Flexíveis</h6>
                                        <h3 class="fw-bold text-dark mb-0">{{
                                            metrics.credit_recovery.fractional_invoices_used }}</h3>
                                    </div>
                                    <div class="icon-shape bg-warning-subtle text-warning-dark rounded-circle">
                                        <i class="bi bi-pie-chart-fill fs-5"></i>
                                    </div>
                                </div>
                                <div class="mt-auto">
                                    <span class="text-muted small">Faturas salvas pelo fracionamento</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- CARDS DE GRÁFICOS (Estrutura adaptada para col-12 com sub-colunas internas e o novo Doughnut Chart) -->
                <div class="row g-4" v-if="metrics">
                    
                    <!-- Linha 1: Desempenho e Evolução (Lado a Lado) -->
                    <div class="col-lg-6">
                        <div class="card p-4 shadow-sm rounded-4 border-0 h-100">
                            <h5 class="fw-bold mb-4 text-dark">Desempenho de Bonificações no Ano</h5>
                            <div style="height: 350px; width: 100%;">
                                <canvas ref="chartCanvas"></canvas>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-6">
                        <div class="card p-4 shadow-sm rounded-4 border-0 h-100">
                            <h5 class="fw-bold mb-4 text-dark">Evolução de Status das Faturas</h5>
                            <div style="height: 350px; width: 100%;">
                                <canvas ref="statusChartCanvas"></canvas>
                            </div>
                        </div>
                    </div>

                    <!-- Linha 2: Meios de Pagamento (Centralizado ou Ocupando largura total) -->
                    <div class="col-lg-12" v-if="metrics.payment_methods_chart">
                        <div class="card p-4 shadow-sm rounded-4 border-0">
                            <h5 class="fw-bold mb-4 text-dark">Meios de Pagamento Utilizados</h5>
                            <div class="row align-items-center">
                                <!-- Lado Esquerdo: O Gráfico de Rosca/Pizza -->
                                <div class="col-md-6 mb-4 mb-md-0 d-flex justify-content-center">
                                    <div style="height: 280px; width: 100%; max-width: 280px;">
                                        <canvas ref="paymentChartCanvas"></canvas>
                                    </div>
                                </div>
                                <!-- Lado Direito: Legenda customizada com percentuais e valores -->
                                <div class="col-md-6">
                                    <ul class="list-group list-group-flush">
                                        <li v-for="item in metrics.payment_methods_chart.chart_data" :key="item.key" 
                                            class="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-2">
                                            <div class="d-flex align-items-center">
                                                <i class="bi bi-circle-fill me-2 fs-6" :style="{ color: getPaymentMethodColor(item.key) }"></i>
                                                <span class="fw-bold text-dark" style="font-size: 0.95rem;">{{ item.label }}</span>
                                            </div>
                                            <div class="text-end">
                                                <span class="d-block fw-bold text-dark">{{ formatCurrency(item.value) }}</span>
                                                <small class="text-muted" style="font-size: 0.8rem;">
                                                    {{ item.percentage_val }}% ({{ item.count }} pgtos)
                                                </small>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import api from '@/services/api';
import Chart from 'chart.js/auto';
import { markRaw } from 'vue';

export default {
    inject: ['globalLoading'],
    name: 'FinancialDashboard',

    data() {
        const today = new Date();
        const currentYear = today.getFullYear();

        // 1. Cria o primeiro dia do ano (Mês 0, Dia 1) e o último dia (Mês 11, Dia 31)
        const firstDay = new Date(currentYear, 0, 1);
        const lastDay = new Date(currentYear, 11, 31);

        // 2. Formata para YYYY-MM-DD blindando contra o bug do fuso horário
        const firstDayStr = firstDay.toLocaleDateString('sv-SE');
        const dateStr = lastDay.toLocaleDateString('sv-SE');

        return {
            activeTab: 'dre',

            loadingDre: false,
            dreData: null,
            costCenters: [],
            dreFilters: {
                start_date: firstDayStr,
                end_date: dateStr,
                cost_center_id: ''
            },

            loading: false,
            metrics: null,
            filters: {
                start_date: firstDayStr,
                end_date: dateStr,
                min_percentage: '',
                max_percentage: '',
            }
        };
    },

    watch: {
        activeTab: {
            immediate: true,
            handler(tab) {
                if (tab === 'dre' && !this.dreData) {
                    this.fetchDreData();
                } else if (tab === 'bonus') {
                    if (!this.metrics) {
                        this.fetchMetrics();
                    } else {
                        this.$nextTick(() => {
                            if (this.metrics.yearly_chart) {
                                this.renderChart(this.metrics.yearly_chart);
                            }
                            if (this.metrics.yearly_status_chart) {
                                this.renderStatusChart(this.metrics.yearly_status_chart);
                            }
                            if (this.metrics.payment_methods_chart) {
                                this.renderPaymentChart(this.metrics.payment_methods_chart.chart_data);
                            }
                        });
                    }
                }
            }
        }
    },

    mounted() {
        this.fetchCostCenters();
    },

    beforeUnmount() {
        this.destroyAllCharts();
    },

    methods: {
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        },

        calculatePercentage(part, total) {
            if (!total || total === 0) return '0.0';
            return ((part / total) * 100).toFixed(1);
        },

        getPaymentMethodColor(key) {
            const colors = {
                'pix': '#0d6efd',         // Azul principal
                'boleto': '#0dcaf0',      // Ciano sutil
                'credit_card': '#10b981', // Verde
            };
            return colors[key] || '#6c757d'; // Cinza fallback
        },

        destroyAllCharts() {
            if (this.chartInstance) {
                this.chartInstance.destroy();
                this.chartInstance = null;
            }
            if (this.statusChartInstance) {
                this.statusChartInstance.destroy();
                this.statusChartInstance = null;
            }
            if (this.paymentChartInstance) {
                this.paymentChartInstance.destroy();
                this.paymentChartInstance = null;
            }
        },

        async fetchDreData() {
            if (!this.dreFilters.start_date || !this.dreFilters.end_date) return;

            this.loadingDre = true;
            try {
                const res = await api.get('/reports/dre', { params: this.dreFilters });
                this.dreData = res.data.data;
            } catch (error) {
                console.error("Erro ao buscar DRE:", error);
            } finally {
                this.loadingDre = false;
            }
        },

        async fetchCostCenters() {
            try {
                const res = await api.get('/cost-centers');
                this.costCenters = res.data.data || res.data;
            } catch (error) {
                console.error("Erro ao buscar centros de custo", error);
            }
        },

        async fetchMetrics() {
            if (!this.filters.start_date || !this.filters.end_date) return;

            this.loading = true;
            this.globalLoading.show('Buscando métricas...');
            try {
                const response = await api.get('financial-analytics', { params: this.filters });
                this.metrics = response.data;
                this.$nextTick(() => {
                    if (response.data.yearly_chart) {
                        this.renderChart(response.data.yearly_chart);
                    }

                    if (this.metrics.yearly_status_chart) {
                        this.renderStatusChart(this.metrics.yearly_status_chart);
                    }

                    if (this.metrics.payment_methods_chart) {
                        this.renderPaymentChart(this.metrics.payment_methods_chart.chart_data);
                    }
                });
            } catch (error) {
                console.error("Erro ao buscar dados financeiros:", error);
            } finally {
                this.loading = false;
                this.globalLoading.hide();
            }
        },

        renderChart(chartData, attempts = 0) {
            const canvas = this.$refs.chartCanvas;
            if (!canvas) {
                if (attempts < 10) {
                    setTimeout(() => this.renderChart(chartData, attempts + 1), 100);
                } else {
                    console.warn("Canvas do gráfico não foi montado a tempo.");
                }
                return;
            }

            if (this.chartInstance) {
                this.chartInstance.destroy();
            }

            const labels = chartData.map(item => item.month_name);
            const discountData = chartData.map(item => item.total_discount);
            const adoptionData = chartData.map(item => item.total_adoptions);

            const chart = new Chart(canvas, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Valor Concedido (R$)',
                            data: discountData,
                            type: 'line',
                            borderColor: '#561569',
                            backgroundColor: 'rgba(33, 51, 97, 0.05)',
                            borderWidth: 3,
                            pointBackgroundColor: '#561569',
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                            pointRadius: 4,
                            tension: 0.4,
                            fill: true,
                            yAxisID: 'y'
                        },
                        {
                            label: 'Qtd de Adesões',
                            data: adoptionData,
                            type: 'bar',
                            backgroundColor: '#e2e8f0',
                            hoverBackgroundColor: '#cbd5e1',
                            borderRadius: 6,
                            borderSkipped: false,
                            yAxisID: 'y1'
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
                        tooltip: { backgroundColor: 'rgba(30, 41, 59, 0.9)', padding: 12, borderRadius: 8 }
                    },
                    scales: {
                        x: { grid: { display: false } },
                        y: {
                            type: 'linear', display: true, position: 'left',
                            grid: { color: '#f1f5f9', drawBorder: false },
                            ticks: { callback: value => 'R$ ' + value }
                        },
                        y1: {
                            type: 'linear', display: true, position: 'right',
                            grid: { drawOnChartArea: false }
                        }
                    }
                }
            });
            this.chartInstance = markRaw(chart);
        },

        renderStatusChart(chartData, attempts = 0) {
            const canvas = this.$refs.statusChartCanvas;
            if (!canvas) {
                if (attempts < 10) setTimeout(() => this.renderStatusChart(chartData, attempts + 1), 100);
                return;
            }

            if (this.statusChartInstance) {
                this.statusChartInstance.destroy();
            }

            // Separando os Eixos de Quantidade
            const labels = chartData.map(item => item.month_name);
            const paidData = chartData.map(item => item.paid);
            const pendingData = chartData.map(item => item.pending);
            const overdueData = chartData.map(item => item.overdue);

            // Separando os Valores Financeiros (Para o Tooltip)
            const paidValues = chartData.map(item => item.paid_value);
            const pendingValues = chartData.map(item => item.pending_value);
            const overdueValues = chartData.map(item => item.overdue_value);

            const chart = new Chart(canvas, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Pagas',
                            data: paidData,
                            customValues: paidValues, // Propriedade customizada injetada
                            type: 'bar',
                            backgroundColor: '#10b981',
                            borderRadius: 4,
                        },
                        {
                            label: 'Pendentes (No Prazo)',
                            data: pendingData,
                            customValues: pendingValues, // Propriedade customizada injetada
                            type: 'bar',
                            backgroundColor: '#3b82f6',
                            borderRadius: 4,
                        },
                        {
                            label: 'Em Atraso',
                            data: overdueData,
                            customValues: overdueValues, // Propriedade customizada injetada
                            type: 'line',
                            borderColor: '#ef4444',
                            backgroundColor: '#ef4444',
                            borderWidth: 3,
                            tension: 0.4,
                            pointRadius: 4,
                            pointBackgroundColor: '#fff',
                            pointBorderColor: '#ef4444',
                            pointBorderWidth: 2,
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
                        tooltip: {
                            backgroundColor: 'rgba(30, 41, 59, 0.95)',
                            padding: 14,
                            borderRadius: 8,
                            titleFont: { size: 14, weight: 'bold' },
                            bodyFont: { size: 13 },
                            callbacks: {
                                label: function (context) {
                                    const label = context.dataset.label || '';
                                    const quantity = context.parsed.y;

                                    // Puxa o valor em Reais usando o customValues que injetamos no dataset
                                    const monetaryValue = context.dataset.customValues[context.dataIndex];

                                    // Formata para a moeda brasileira
                                    const formattedMoney = new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                    }).format(monetaryValue);

                                    return `${label}: ${quantity} fatura(s)  →  ${formattedMoney}`;
                                }
                            }
                        }
                    },
                    scales: {
                        x: { grid: { display: false }, stacked: false },
                        y: {
                            beginAtZero: true,
                            grid: { color: '#f1f5f9', drawBorder: false },
                            ticks: { precision: 0 } // Mostra apenas números inteiros no eixo
                        }
                    }
                }
            });

            this.statusChartInstance = markRaw(chart);
        },

        renderPaymentChart(chartData, attempts = 0) {
            const canvas = this.$refs.paymentChartCanvas;
            if (!canvas) {
                if (attempts < 10) {
                    setTimeout(() => this.renderPaymentChart(chartData, attempts + 1), 100);
                }
                return;
            }

            if (this.paymentChartInstance) {
                this.paymentChartInstance.destroy();
            }

            const labels = chartData.map(item => item.label);
            const values = chartData.map(item => item.value);
            const bgColors = chartData.map(item => this.getPaymentMethodColor(item.key));

            const chart = new Chart(canvas, {
                type: 'doughnut', // Formato de Rosca elegante
                data: {
                    labels: labels,
                    datasets: [{
                        data: values,
                        backgroundColor: bgColors,
                        borderWidth: 2,
                        borderColor: '#ffffff',
                        hoverOffset: 6
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false // Desativado para usar a nossa legenda customizada em HTML ao lado
                        },
                        tooltip: {
                            backgroundColor: 'rgba(30, 41, 59, 0.95)',
                            padding: 12,
                            borderRadius: 8,
                            callbacks: {
                                label: (context) => {
                                    const index = context.dataIndex;
                                    const rawValue = context.raw;
                                    const percentage = chartData[index].percentage_val;
                                    const count = chartData[index].count;
                                    const formattedValue = this.formatCurrency(rawValue);

                                    return ` ${context.label}: ${formattedValue} (${percentage}%) → ${count} pgtos`;
                                }
                            }
                        }
                    },
                    cutout: '65%' // Deixa o gráfico em rosca vazada elegante
                }
            });

            this.paymentChartInstance = markRaw(chart);
        }
    }
};
</script>

<style scoped>
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02) !important;
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
}

.btn-pmg-primary:hover {
    background: #1a284c;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

.kpi-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.04) !important;
}

.icon-shape {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bg-primary-subtle {
    background-color: #eff6ff;
}

.bg-success-subtle {
    background-color: #f0fdf4;
}

.bg-info-subtle {
    background-color: #f0f9ff;
}

.bg-warning-subtle {
    background-color: #fffbeb;
}

.text-primary {
    color: #2563eb !important;
}

.text-success {
    color: #16a34a !important;
}

.text-info {
    color: #0284c7 !important;
}

.text-warning-dark {
    color: #d97706 !important;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}
</style>